import { httpClient } from "common/HttpClient/http-client";
import { getRequest, patchRequest, postRequest } from "common/HttpClient/http-utility";
import {
  isFiniteNumber,
  positiveFiniteNumberOr,
  trimmedParam,
} from "common/utils/valueChecks";
import {
  getCanonicalTagIdForTagRow,
  resolveContextNodeIdForTagRow,
} from "./tagRowsMapper";
import { TAG_SOURCE_CONNECTORS } from "pages/Admin/Groups/GroupsConstant";

const CONTEXT_NODES_BASE = "/platform-admin-service/v1/context-nodes";
const TAGS_BASE = "/platform-admin-service/v1/tags";
const BULK_CONTEXTUALIZE_UNIFIED = `${TAGS_BASE}/bulk-contextualize`;
const NODE_TYPES_REFERENCE = "/platform-admin-service/v1/reference-data/node-types";

const DEFAULT_PAGE_SIZE = 250;
const BULK_CONTEXTUALIZE_MAX_TAGS = 250;

export function extractPagedNodesList(payload) {
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.content)) return payload.content;
  if (Array.isArray(payload?.data)) return payload.data;
  if (Array.isArray(payload?.data?.content)) return payload.data.content;
  return [];
}

function extractTotalPages(payload) {
  const pageMeta =
    payload?.metadata ??
    payload?.pagination ??
    payload?.page ??
    payload?.data?.page ??
    payload?.data?.metadata ??
    {};
  const totalPages = pageMeta.totalPages;
  if (typeof totalPages === "number" && totalPages > 0) return totalPages;
  return 1;
}

export function extractTotalRowCountFromPagedPayload(payload) {
  if (payload == null || typeof payload !== "object" || Array.isArray(payload)) {
    return null;
  }
  const pageMeta =
    payload.metadata ??
    payload.pagination ??
    payload.page ??
    payload.data?.page ??
    payload.data?.metadata ??
    {};
  const total =
    pageMeta.totalItems ??
    pageMeta.totalCount ??
    pageMeta.totalElements ??
    (typeof pageMeta.total === "number" ? pageMeta.total : null);
  if (typeof total === "number" && total >= 0) return total;
  return null;
}

const TAGS_LIST_STRING_PARAMS = [
  "nodeName",
  "nodeType",
  "nodeSubType",
  "sourcePath",
  "sourceTagId",
  "sourceConnector",
  "source",
  "parentNodeName",
  "search",
];

export function buildTagsListQuery(params = {}) {
  const query = {
    page: params.page ?? 0,
    size: params.size ?? DEFAULT_PAGE_SIZE,
    sort: params.sort ?? "displayName,asc",
  };

  for (const key of TAGS_LIST_STRING_PARAMS) {
    const value = trimmedParam(params, key);
    if (value !== "") query[key] = value;
  }

  if (typeof params.isContextualized === "boolean") {
    query.isContextualized = params.isContextualized;
  }

  return query;
}

export function buildContextNodesListQuery(params = {}) {
  return {
    page: params.page ?? 0,
    size: params.size ?? DEFAULT_PAGE_SIZE,
    sort: params.sort ?? "id,asc",
    ...(params.search != null && params.search !== ""
      ? { search: params.search }
      : {}),
    ...(params.edgeTag != null && params.edgeTag !== ""
      ? { edgeTag: params.edgeTag }
      : {}),
    ...(params.nodeType != null && params.nodeType !== ""
      ? { nodeType: params.nodeType }
      : {}),
    ...(params.nodeName != null && params.nodeName !== ""
      ? { nodeName: params.nodeName }
      : {}),
    ...(params.sparkplugMetricTagContains != null &&
    params.sparkplugMetricTagContains !== ""
      ? { sparkplugMetricTagContains: params.sparkplugMetricTagContains }
      : {}),
  };
}

function normalizeTagRowSourceConnector(row) {
  return String(
    row?.source ?? row?.sourceConnector ?? row?.sourceSystem ?? ""
  )
    .trim()
    .toLowerCase();
}

function pickContextNodeFromSearchResults(list, row) {
  if (!Array.isArray(list) || list.length === 0) {
    return null;
  }
  if (list.length === 1) {
    return list[0];
  }
  const canonicalTagId = getCanonicalTagIdForTagRow(row);
  if (canonicalTagId) {
    const byEdgeTag = list.find(
      (node) => String(node?.edgeTag ?? "").trim() === canonicalTagId
    );
    if (byEdgeTag) {
      return byEdgeTag;
    }
  }
  const displayName = String(row?.nodeName ?? "").trim().toLowerCase();
  if (displayName) {
    const nameMatches = list.filter(
      (node) => String(node?.nodeName ?? "").trim().toLowerCase() === displayName
    );
    if (nameMatches.length === 1) {
      return nameMatches[0];
    }
  }
  return list[0];
}

async function searchContextNodes(params) {
  const response = await fetchContextNodesPage({
    page: 0,
    size: 10,
    sort: "id,asc",
    ...params,
  });
  if (response.error || response.status >= 400) {
    return {
      error: response.error ?? "Failed to search context nodes",
      data: null,
    };
  }
  return { data: extractPagedNodesList(response.data ?? {}) };
}

export function buildTagsExportQuery(tableFilters = {}, sort = "displayName,asc") {
  const listQuery = buildTagsListQuery({
    nodeName: tableFilters.nodeName,
    nodeType: tableFilters.nodeType,
    nodeSubType: tableFilters.nodeSubType,
    sourcePath: tableFilters.edgeTag,
    sourceTagId: tableFilters.sourceTagId,
    sourceConnector: tableFilters.sourceSystem,
    parentNodeName: tableFilters.parentNodeName,
    search: tableFilters.search,
    page: 0,
    size: 1,
    sort,
  });
  const { page, size, ...filters } = listQuery;
  return {
    export: true,
    download: true,
    ...filters,
  };
}

export async function fetchTagsPage(params) {
  return getRequest(TAGS_BASE, buildTagsListQuery(params));
}

export async function fetchContextNodesPage(params) {
  return getRequest(CONTEXT_NODES_BASE, buildContextNodesListQuery(params));
}

export async function fetchAllTagContextNodes(queryOverrides = {}) {
  const aggregated = [];
  let pageNumber = 0;
  let totalPages = 1;

  while (pageNumber < totalPages) {
    const response = await fetchContextNodesPage({
      page: pageNumber,
      size: DEFAULT_PAGE_SIZE,
      sort: "id,asc",
      ...queryOverrides,
    });

    if (response.error || response.status >= 400) {
      return {
        status: response.status ?? 500,
        error: response.error ?? "Failed to load tags",
        data: null,
      };
    }

    const payload = response.data ?? {};
    const content = extractPagedNodesList(payload);
    totalPages = extractTotalPages(payload);

    aggregated.push(...content);
    pageNumber += 1;

    if (content.length === 0) {
      break;
    }
  }

  return { status: 200, data: aggregated };
}

export async function fetchParentCandidates(allowedParentTypeNames, options = {}) {
  const pageSize = positiveFiniteNumberOr(options.pageSize, DEFAULT_PAGE_SIZE);

  const allowedSet = new Set(
    allowedParentTypeNames
      .map((t) => String(t ?? "").trim().toLowerCase())
      .filter((t) => t !== "")
  );
  if (allowedSet.size === 0) {
    return { status: 200, data: [] };
  }

  const byId = new Map();
  let pageNumber = 0;
  let totalPages = 1;

  while (pageNumber < totalPages) {
    const response = await fetchContextNodesPage({
      page: pageNumber,
      size: pageSize,
      sort: "id,asc",
    });

    if (response.error || response.status >= 400) {
      return {
        status: response.status ?? 500,
        error: response.error ?? "Failed to load parent candidates",
        data: null,
      };
    }

    const payload = response.data ?? {};
    const content = extractPagedNodesList(payload);
    totalPages = extractTotalPages(payload);

    for (const dto of content) {
      const nt = String(dto?.nodeType ?? "").toLowerCase();
      if (!allowedSet.has(nt)) continue;
      const id = dto?.nodeId;
      if (id == null) continue;
      const key = String(id);
      if (!byId.has(key)) {
        const parentIdRaw = dto?.parentNodeId;
        const parentNodeId = isFiniteNumber(parentIdRaw)
          ? Number(parentIdRaw)
          : null;
        byId.set(key, {
          nodeId: id,
          nodeName:
            dto.nodeName == null || dto.nodeName === ""
              ? ""
              : String(dto.nodeName),
          nodeType:
            dto.nodeType == null || dto.nodeType === ""
              ? ""
              : String(dto.nodeType),
          parentNodeId,
        });
      }
    }

    pageNumber += 1;
    if (content.length === 0) break;
  }

  return { status: 200, data: [...byId.values()] };
}

export async function fetchNodeTypesReference() {
  return getRequest(NODE_TYPES_REFERENCE);
}

export async function fetchContextNodeById(nodeId) {
  if (nodeId == null || String(nodeId).trim() === "") {
    return { error: "Missing node id" };
  }
  return getRequest(`${CONTEXT_NODES_BASE}/${nodeId}`);
}

async function lookupContextNodeDtoForTagRow(row) {
  if (row == null || row.uncontextualized) {
    return null;
  }

  const contextNodeId = resolveContextNodeIdForTagRow(row);
  if (contextNodeId != null) {
    const result = await fetchContextNodeById(contextNodeId);
    if (result?.error || !result?.data) {
      return null;
    }
    return result.data;
  }

  const source = normalizeTagRowSourceConnector(row);
  const canonicalTagId = getCanonicalTagIdForTagRow(row);

  if (source === TAG_SOURCE_CONNECTORS.MQTT_SPB && canonicalTagId) {
    const search = await searchContextNodes({
      nodeType: "tag",
      sparkplugMetricTagContains: canonicalTagId,
    });
    if (search.error) {
      return null;
    }
    return pickContextNodeFromSearchResults(search.data, row);
  }

  if (source === TAG_SOURCE_CONNECTORS.HACHWIMS) {
    const nodeName = String(row?.nodeName ?? "").trim();
    if (nodeName === "") {
      return null;
    }
    const search = await searchContextNodes({
      nodeType: "tag",
      nodeName,
    });
    if (search.error) {
      return null;
    }
    return pickContextNodeFromSearchResults(search.data, row);
  }

  return null;
}

export async function resolveContextNodeIdForTagRowAsync(row) {
  const dto = await lookupContextNodeDtoForTagRow(row);
  const nodeId = dto?.nodeId;
  return isFiniteNumber(nodeId) ? Number(nodeId) : null;
}

export async function fetchContextNodeForTagRow(row) {
  if (row == null || row.uncontextualized) {
    return { error: "Row is not contextualized" };
  }
  const dto = await lookupContextNodeDtoForTagRow(row);
  if (!dto) {
    return { error: "Could not resolve context node id" };
  }
  return { data: dto };
}

export async function patchContextNodes(body) {
  return patchRequest(CONTEXT_NODES_BASE, body);
}

export async function bulkContextualizeUnifiedTags(body) {
  return postRequest(BULK_CONTEXTUALIZE_UNIFIED, body);
}

export { BULK_CONTEXTUALIZE_MAX_TAGS };

export async function exportTagsCsv({ tableFilters, sort } = {}) {
  try {
    const params = buildTagsExportQuery(tableFilters, sort);
    const response = await httpClient.get(TAGS_BASE, {
      params,
      responseType: "blob",
      timeout: 0,
      headers: { Accept: "text/csv" },
    });
    return { status: response.status, data: response.data };
  } catch (error) {
    return {
      status: error?.response?.status ?? 500,
      error: error?.response?.data?.message ?? error?.message ?? "Export failed",
    };
  }
}
